/* Story sections — hybrid listicle pages (fw-hybrid).
   <story-sticky-cta> follows the proven listicle choreography:
     - appears once the reader reaches the Nth story item (attribute
       arm-item, default 2), so skimmers get an early path to the offer;
       falls back to 30% document scroll when no story items exist;
     - hides while approaching and reading the target shop section;
     - reappears below the shop section (proof, how-to, FAQ still sell);
     - hides again near the footer.
   The button smooth-scrolls to the anchor (default #offer). */

(() => {
  if (customElements.get('story-sticky-cta')) return;

  class StoryStickyCta extends HTMLElement {
    connectedCallback() {
      this._armed = false;
      this._nearShop = false;
      this._nearFooter = false;
      this._pastEnd = false;

      this._anchor = document.querySelector(this.getAttribute('anchor') || '#offer');

      this._onClick = (event) => {
        if (!this._anchor) return;
        event.preventDefault();
        const shop = this._shopTarget;
        if (shop && shop.getBoundingClientRect().bottom < 0) {
          // Reader is below the shop section: scroll back UP and center the
          // buy area so the CTA and offer stack land together in view.
          const buy = shop.querySelector('buy-buttons') || this._anchor;
          buy.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          this._anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };
      this._button = this.querySelector('a[href^="#"], [data-scroll]');
      this._button?.addEventListener('click', this._onClick);

      this._observers = [];

      // ── Arm: the Nth story item reaches the upper quarter of the viewport.
      const armIndex = parseInt(this.getAttribute('arm-item') || '2', 10) - 1;
      const items = document.querySelectorAll('.shopify-section--story-item');
      const armTarget = items[Math.min(armIndex, items.length - 1)];

      if (armTarget) {
        const armObserver = new IntersectionObserver(
          ([entry]) => {
            this._armed = entry.isIntersecting || entry.boundingClientRect.top < 0;
            this._update();
          },
          { rootMargin: '0px 0px -75% 0px' }
        );
        armObserver.observe(armTarget);
        this._observers.push(armObserver);
      } else {
        // Fallback: arm past 30% document scroll (the 3P listicle rule).
        this._onScroll = () => {
          const doc = document.documentElement;
          const max = doc.scrollHeight - window.innerHeight;
          this._armed = max > 0 && (window.scrollY / max) * 100 > 30;
          this._update();
        };
        window.addEventListener('scroll', this._onScroll, { passive: true });
        this._onScroll();
      }

      // ── Hide while approaching / inside the shop section.
      // The anchor lives in a zero-height shim section; the shop section is
      // the next section after it. Observing the shim alone un-hides the bar
      // while the (tall) buy box is still on screen.
      const anchorSection = this._anchor?.closest('.shopify-section');
      const shopTarget = anchorSection?.nextElementSibling || anchorSection || this._anchor;
      this._shopTarget = shopTarget;
      if (shopTarget) {
        const shopObserver = new IntersectionObserver(
          ([entry]) => {
            this._nearShop = entry.isIntersecting;
            this._update();
          },
          { rootMargin: '0px 0px 200px 0px' }
        );
        shopObserver.observe(shopTarget);
        this._observers.push(shopObserver);
      }

      // ── Hide near the footer.
      const footer = document.querySelector('.shopify-section--footer, footer');
      if (footer) {
        const footerObserver = new IntersectionObserver(
          ([entry]) => {
            this._nearFooter = entry.isIntersecting;
            this._update();
          },
          { rootMargin: '0px 0px 100px 0px' }
        );
        footerObserver.observe(footer);
        this._observers.push(footerObserver);
      }

      // ── Optional (hide-from="<selector>"): hide as the reader reaches that
      // section, a little before it enters the screen, and stay hidden below it.
      // Read on scroll (not an observer) so a fast jump back up still restores the bar.
      const hideFrom = this.getAttribute('hide-from');
      const endTarget = hideFrom ? document.querySelector(hideFrom) : null;
      if (endTarget) {
        this._onEndScroll = () => {
          if (this._endFrame) return;
          this._endFrame = requestAnimationFrame(() => {
            this._endFrame = null;
            const past = endTarget.getBoundingClientRect().top < window.innerHeight + 80;
            if (past !== this._pastEnd) {
              this._pastEnd = past;
              this._update();
            }
          });
        };
        window.addEventListener('scroll', this._onEndScroll, { passive: true });
        this._onEndScroll();
      }
    }

    _update() {
      this.toggleAttribute('armed', this._armed);
      this.toggleAttribute('suppressed', this._nearShop || this._nearFooter || this._pastEnd);
    }

    disconnectedCallback() {
      this._observers?.forEach((observer) => observer.disconnect());
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onEndScroll) window.removeEventListener('scroll', this._onEndScroll);
      this._button?.removeEventListener('click', this._onClick);
    }
  }

  customElements.define('story-sticky-cta', StoryStickyCta);
})();
